import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, ArrowRight, Database, Settings } from 'lucide-react';
import { useArcheAdmin } from '@/auth/hooks/useArcheAdmin';
import { useGameStates } from '../hooks';
import { GameStateCard } from '../components';
import { ArcheHeader } from '../../../components/ArcheHeader';

export function GameStateListPage() {
  const { states, loading, error } = useGameStates();
  const navigate = useNavigate();
  // Server route gate: `requireAdminOrLitcraftAdmin` (docs/api-admin.md).
  const isAdmin = useArcheAdmin('litcraft_admin');

  if (loading) {
    return (
      <div className="flex flex-col h-full">
        <ArcheHeader archeId="arche_dfe_game_state" title="Game State" />
        <div className="p-6 max-w-4xl mx-auto space-y-4 w-full">
          <Skeleton className="h-8 w-48" />
          <div className="grid gap-4 md:grid-cols-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-28" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col h-full">
        <ArcheHeader archeId="arche_dfe_game_state" title="Game State" />
        <div className="p-6 max-w-4xl mx-auto w-full">
          <Card className="border-destructive">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-5 w-5" />
                Error
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">{error}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <ArcheHeader archeId="arche_dfe_game_state" title="Game State" />
      <div className="p-6 max-w-4xl mx-auto space-y-6 w-full">
        <p className="text-muted-foreground">
          Persistent key-value store for your game data. {states.length} keyword
          {states.length !== 1 ? 's' : ''} stored.
        </p>

        {states.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <Database className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium">No game state stored</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Game state data will appear here when games start saving state.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {states.map((state) => (
              <GameStateCard
                key={state.gameStateId}
                state={state}
                onClick={() => navigate(`/a/arche_dfe_game_state/${state.keyword}`)}
              />
            ))}
          </div>
        )}

        {isAdmin && (
          <Card
            className="hover:bg-muted/50 transition-colors cursor-pointer"
            onClick={() => navigate('/a/arche_dfe_game_state/admin')}
          >
            <CardContent className="flex items-center justify-between py-6">
              <div className="flex items-center gap-3">
                <Settings className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">Admin</p>
                  <p className="text-sm text-muted-foreground">
                    Look up user game states and browse audit log
                  </p>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
